import { useTranslation } from "react-i18next";
import { ArrowRight, Sprout } from "lucide-react";
import { cn } from "../../../lib/utils";
import type { IdentitySpace } from "../../../api/identitySpaces";
import type { UserTakeaway } from "../../../api/userTakeaways";
import { projectOf, type ProjectRef } from "../../../lib/projectIndex";

const FALLBACK_COLOR = "#6366f1";

/** Area color of a project — id match first, then the seeder's spaceName. */
export const projectColor = (project: ProjectRef, spaces: IdentitySpace[] | undefined): string =>
  (spaces ?? []).find(
    (s) => s._id === project.spaceId || (project.spaceName != null && s.name === project.spaceName)
  )?.color || FALLBACK_COLOR;

/** Where a project chip should open: the Project Room, or the journey's Overview lens. */
export const projectPath = (project: ProjectRef): string =>
  project.isJourney ? `/pwf?overview=${project.id}` : `/project/${project.id}`;

/** A project's own label, wherever the project (not a takeaway) is already
 *  known — theme attribution, or any future "planted in" surface that isn't
 *  reading off a takeaway. `ProjectChip` below wraps this for the takeaway
 *  case. */
export const ProjectPill = ({
  project,
  color,
  variant = "mini",
  className,
}: {
  project: ProjectRef;
  color: string;
  variant?: "mini" | "badge";
  className?: string;
}) => {
  const { t } = useTranslation();
  return (
    <span
      title={t("workspace_project_chip_title", { title: project.title })}
      className={cn(
        "inline-flex items-center gap-1 rounded-full max-w-[160px]",
        variant === "mini" ? "text-[10.5px] px-1.5 py-px" : "h-5 text-[10px] px-1.5 border bg-white/70",
        className
      )}
      style={variant === "mini" ? { color, background: `${color}14` } : { color, borderColor: `${color}50` }}
    >
      <Sprout className="w-2.5 h-2.5 flex-none" />
      <span className="truncate">{project.title}</span>
    </span>
  );
};

/** "🌱 {project}" — where a takeaway was planted. A label, not a control: the
 *  row it sits in is the tap target; detail panes get ProjectLink instead. */
export const ProjectChip = ({
  item,
  projectById,
  spaces,
  variant = "mini",
  className,
}: {
  item: UserTakeaway;
  projectById: Record<string, ProjectRef>;
  spaces: IdentitySpace[] | undefined;
  variant?: "mini" | "badge";
  className?: string;
}) => {
  const project = projectOf(projectById, item);
  if (!project) return null;
  return <ProjectPill project={project} color={projectColor(project, spaces)} variant={variant} className={className} />;
};

/** The detail-pane counterpart: a real ≥44px control that opens the project. */
export const ProjectLink = ({
  item,
  projectById,
  onOpen,
}: {
  item: UserTakeaway;
  projectById: Record<string, ProjectRef>;
  onOpen: (project: ProjectRef) => void;
}) => {
  const { t } = useTranslation();
  const project = projectOf(projectById, item);
  if (!project) return null;
  return (
    <button
      type="button"
      onClick={() => onOpen(project)}
      className="inline-flex items-center gap-1.5 min-h-[44px] px-1 text-[12.5px] text-primary/80 hover:text-primary"
    >
      <Sprout className="w-3.5 h-3.5" />
      {t("workspace_open_project", { title: project.title })}
      <ArrowRight className="w-3 h-3 rtl:rotate-180" />
    </button>
  );
};
